import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Copy, Check, Clock, CheckCircle2, CircleAlert } from "lucide-react";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const brl = (n) => new Intl.NumberFormat("pt-BR", { style: "currency", currency: "BRL" }).format(Number(n || 0));
// Remove tag interna de fornecedor (formato " [Fornecedor]" ou " [Fornecedor - obs]")
// que aparece em itens avulsos cadastrados em Vendas. Cliente NÃO pode ver isso.
// Aceita a tag em qualquer posição do texto (fim ou meio), não só no final.
const cleanItemName = (n) => (n ? String(n).replace(/\s*\[[^\[\]]*\]\s*/g, " ").replace(/\s+/g, " ").trim() : "");

const STATUS_META = {
  Pendente:   { label: "Aguardando pagamento", tone: "amber",   Icon: Clock },
  Separando:  { label: "Aguardando pagamento", tone: "amber",   Icon: Clock },
  Aguardando: { label: "Aguardando pagamento", tone: "amber",   Icon: Clock },
  Pago:       { label: "Pagamento confirmado", tone: "emerald", Icon: CheckCircle2 },
  Entregue:   { label: "Pedido entregue",      tone: "emerald", Icon: CheckCircle2 },
  Finalizado: { label: "Pedido finalizado",    tone: "emerald", Icon: CheckCircle2 },
  Cancelada:  { label: "Pedido cancelado",     tone: "red",     Icon: CircleAlert },
};

export default function Pagamento() {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [copiedKey, setCopiedKey] = useState(false);
  const [copiedBr, setCopiedBr] = useState(false);

  const load = async () => {
    try {
      const r = await axios.get(`${API}/public/payment/${token}`);
      setData(r.data);
    } catch (e) {
      setError(e.response?.status === 404 ? "Pedido não encontrado" : "Erro ao carregar o pedido");
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [token]);

  // Auto-refresh a cada 20s para mostrar quando o lojista marcar como "Pago"
  useEffect(() => {
    const i = setInterval(load, 20000);
    return () => clearInterval(i);
    /* eslint-disable-next-line */
  }, [token]);

  const copy = async (text, which) => {
    try {
      await navigator.clipboard.writeText(text);
      if (which === "key") { setCopiedKey(true); setTimeout(()=>setCopiedKey(false), 2500); }
      if (which === "br")  { setCopiedBr(true);  setTimeout(()=>setCopiedBr(false), 2500); }
      toast.success("Copiado!");
    } catch {
      toast.error("Não foi possível copiar. Selecione e copie manualmente.");
    }
  };

  if (loading) return (
    <div className="min-h-screen flex items-center justify-center bg-neutral-50">
      <p className="text-sm text-neutral-500">Carregando pedido…</p>
    </div>
  );
  if (error) return (
    <div className="min-h-screen flex items-center justify-center bg-neutral-50 p-6">
      <Card className="p-8 max-w-md text-center border-[#E5E5E5] shadow-none">
        <CircleAlert size={32} className="mx-auto text-red-500"/>
        <h1 className="font-display text-2xl mt-4">{error}</h1>
        <p className="text-sm text-neutral-500 mt-2">Verifique o link e tente novamente.</p>
      </Card>
    </div>
  );

  const { order, store, pix } = data;
  const meta = STATUS_META[order.status] || { label: order.status, tone: "neutral", Icon: Clock };
  const toneClasses = {
    amber: "bg-amber-100 text-amber-800 border-amber-200",
    emerald: "bg-emerald-100 text-emerald-800 border-emerald-200",
    red: "bg-red-100 text-red-800 border-red-200",
    neutral: "bg-neutral-100 text-neutral-800 border-neutral-200",
  }[meta.tone];

  return (
    <div className="min-h-screen bg-neutral-50 py-8 px-4" data-testid="payment-page">
      <div className="max-w-lg mx-auto space-y-4">
        {/* Header: logo/store name */}
        <div className="text-center">
          {store.logo && <img src={store.logo} alt={store.name} className="w-16 h-16 object-cover rounded-full mx-auto border border-[#E5E5E5]"/>}
          <h1 className="font-display text-2xl mt-3">{store.name || "Loja"}</h1>
          <p className="text-xs text-neutral-500">Pedido #{String(order.order_number || "").padStart(6, "0")}</p>
        </div>

        {/* Status badge */}
        <Card className="p-5 border-[#E5E5E5] shadow-none">
          <div className={`flex items-center gap-3 rounded-md border px-4 py-3 ${toneClasses}`} data-testid="payment-status-badge">
            <meta.Icon size={20}/>
            <div className="flex-1">
              <div className="text-xs opacity-80 uppercase tracking-wider">Status</div>
              <div className="font-medium text-base">{meta.label}</div>
            </div>
          </div>
          {order.customer_name && <p className="text-xs text-neutral-500 mt-3">Cliente: <span className="font-medium text-neutral-700">{order.customer_name}</span></p>}
        </Card>

        {/* Itens do pedido */}
        <Card className="p-5 border-[#E5E5E5] shadow-none">
          <h2 className="label-tiny mb-3">Itens do pedido</h2>
          <div className="space-y-2 divide-y divide-neutral-100">
            {order.items.map((it, idx) => (
              <div key={idx} className="flex justify-between items-start pt-2 text-sm first:pt-0" data-testid={`pay-item-${idx}`}>
                <div className="min-w-0 flex-1 pr-3">
                  <div className="truncate">{it.quantity}× {cleanItemName(it.name)}</div>
                  <div className="text-xs text-neutral-500">{brl(it.unit_price)} un.</div>
                </div>
                <div className="whitespace-nowrap">{brl(it.unit_price * it.quantity)}</div>
              </div>
            ))}
          </div>
          <div className="border-t border-[#E5E5E5] mt-3 pt-3 space-y-1 text-sm">
            {order.discount > 0 && (
              <div className="flex justify-between text-neutral-500"><span>Desconto</span><span>−{brl(order.discount)}</span></div>
            )}
            <div className="flex justify-between font-display text-xl pt-1"><span>Total</span><span data-testid="pay-total">{brl(order.total)}</span></div>
          </div>
        </Card>

        {/* PIX Copia e Cola */}
        {pix ? (
          <Card className="p-5 border-[#E5E5E5] shadow-none" data-testid="pix-card">
            <h2 className="label-tiny">Pagar com PIX</h2>
            <p className="text-xs text-neutral-500 mt-1">Copie a chave ou o código Copia e Cola abaixo e cole no app do seu banco.</p>

            {pix.holder_name && (
              <div className="mt-3 text-xs">
                <span className="text-neutral-500">Recebedor:</span> <span className="font-medium">{pix.holder_name}</span>
              </div>
            )}

            {/* Copia e Cola (preferido — já traz valor) */}
            <div className="mt-4">
              <div className="flex items-center justify-between mb-1">
                <span className="text-xs font-medium">PIX Copia e Cola</span>
                <span className="text-[10px] text-emerald-700 bg-emerald-100 border border-emerald-200 rounded-full px-2 py-0.5">Valor já preenchido</span>
              </div>
              <div className="relative">
                <div className="text-[10px] font-mono break-all bg-neutral-50 border border-[#E5E5E5] rounded-md p-3 pr-12 max-h-24 overflow-y-auto select-all" data-testid="pix-brcode-text">
                  {pix.brcode}
                </div>
                <Button
                  size="sm"
                  onClick={()=>copy(pix.brcode, "br")}
                  className="absolute right-2 top-2 bg-[#0A0A0A] hover:bg-black text-white h-8 px-2"
                  data-testid="copy-brcode-button"
                >
                  {copiedBr ? <><Check size={12} className="mr-1"/> OK</> : <><Copy size={12} className="mr-1"/> Copiar</>}
                </Button>
              </div>
            </div>

            {/* Chave PIX (apenas a chave) */}
            <div className="mt-4">
              <div className="text-xs font-medium mb-1">Chave PIX</div>
              <div className="flex items-stretch gap-2">
                <div className="flex-1 bg-neutral-50 border border-[#E5E5E5] rounded-md px-3 py-2 text-sm font-mono break-all" data-testid="pix-key-text">
                  {pix.key}
                </div>
                <Button variant="outline" size="sm" onClick={()=>copy(pix.key, "key")} data-testid="copy-pix-key-button">
                  {copiedKey ? <Check size={14}/> : <Copy size={14}/>}
                </Button>
              </div>
            </div>

            <div className="mt-4 text-[11px] text-neutral-500 bg-amber-50 border border-amber-100 rounded-md p-3">
              Após efetuar o pagamento, <strong>aguarde a confirmação</strong> do lojista nessa página
              — o status atualiza automaticamente quando o pagamento for identificado.
            </div>
          </Card>
        ) : (
          order.payment_method === "PIX" && order.status !== "Pago" && order.status !== "Finalizado" && (
            <Card className="p-5 border-amber-200 bg-amber-50 shadow-none">
              <p className="text-xs text-amber-800">
                A chave PIX ainda não foi configurada pelo lojista. Entre em contato para receber os dados de pagamento.
              </p>
            </Card>
          )
        )}

        {/* Rodapé contato */}
        {store.phone && (
          <div className="text-center text-[11px] text-neutral-400 pt-2 pb-8">
            Dúvidas? WhatsApp: <span className="font-medium text-neutral-600">{store.phone}</span>
          </div>
        )}
      </div>
    </div>
  );
}
